import merge from 'lodash/merge';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import styled from 'styled-components';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import Carousel from 'components/domain/carousel/Carousel';
import {
  ConfirmationModal,
  useConfirmation,
} from 'components/domain/confirmation';
import Container from 'components/domain/container/Container';
import WishlistAddFormContainer from 'components/domain/wishlist/WishlistAddFormContainer';
import WishlistEditFormContainer from 'components/domain/wishlist/WishlistEditFormContainer';
import WishlistOverviewSkeleton from 'components/domain/wishlist/WishlistOverviewSkeleton.skeleton';
import {
  SCROLL_OFFSET,
  useWishlistContentScrollContext,
} from 'components/domain/wishlist/WishlistScrollableContext';
import { useRemoveWishlist } from 'fdr/components/candidate/fdr-wishlist/fdr-use-remove-wishlist';
import useSetActiveWishlist from 'fdr/components/candidate/fdr-wishlist/fdr-use-set-active-wishlist';
import { useWishlistOverview } from 'fdr/components/candidate/fdr-wishlist/fdr-use-wishlist-overview';
import { IWishlistItem } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist.types';
import { FdrWishlistItemDesktop } from './fdr-wishlist-item-desktop';
import FdrWishlistNoAuthNotification from './fdr-wishlist-no-auth-notification';

const SlideWidth = 268;
const SlideGap = 32;

export const WishlistOverviewWrapper = styled(Container).attrs({
  'data-id': `wishlist-overview-wrapper`,
})<{ withTopPadding: boolean; withReducedWidth: boolean }>(
  ({ withTopPadding, theme }) =>
    sx2CssThemeFn({
      pt: withTopPadding ? [4, '65px'] : 0,
      px: 4,
      '.wishlist-title': merge({}, theme.text.groupHeading, {
        display: 'flex',
        mb: [3, 5],
        mt: 3,
      }),
      '.wishlist-title-new': {
        pt: 5,
        border: 'none',
        mb: [3, 4],
        mt: 0,
      },
      '.wishlist-items-wrapper': {
        display: 'flex',
        mb: [4, 5],
        flexWrap: 'wrap',
      },
    }),
  ({ withReducedWidth }) =>
    withReducedWidth
      ? {
          '.wishlist-items-carousel': {
            width: `${SlideWidth + SlideGap}px`,
          },
        }
      : {}
);

export const FdrWishlistOverviewDesktop: React.FC = () => {
  const [t] = useTranslation(['common', 'wishlist']);
  const { channelType } = useAuthentication();
  const { removeWishlist } = useRemoveWishlist();
  const { setActiveWishlist } = useSetActiveWishlist();
  const { wishlists, loading } = useWishlistOverview();
  const { listRef } = useWishlistContentScrollContext();
  const isAuthenticated = channelType === AuthChannelType.B2C;
  const isAnonymous = channelType === AuthChannelType.B2CAnonymous;

  const [showConfirmation, confirmationProps] = useConfirmation(
    removeWishlist,
    {
      closeOnApply: true,
    }
  );

  const editable = channelType !== AuthChannelType.B2CAnonymous;
  const [editedWishlist, setEditedWishlist] =
    useState<IWishlistItem | null>(null);
  const [deletedWishlist, setDeletedWishlist] =
    useState<IWishlistItem | null>(null);

  const wishlistItems = wishlists?.map(wishlist => (
    <FdrWishlistItemDesktop
      key={wishlist.id}
      {...wishlist}
      image={wishlist?.image}
      editable={editable}
      onClick={async id => {
        await setActiveWishlist(id);

        if (listRef.current) {
          window.scrollTo({
            top: listRef.current.offsetTop - SCROLL_OFFSET,
            behavior: 'smooth',
          });
        }
      }}
      onRemove={e => {
        e.stopPropagation();
        setDeletedWishlist(wishlist);
        showConfirmation();
      }}
      onEdit={e => {
        e.stopPropagation();
        setEditedWishlist(wishlist);
      }}
    />
  ));

  if (loading) {
    return <WishlistOverviewSkeleton />;
  }

  return (
    <WishlistOverviewWrapper
      withTopPadding={!isAuthenticated}
      withReducedWidth={isAnonymous}
    >
      <h2
        data-id="wishlist-groupName"
        className={`wishlist-title wishlist-title-new`}
      >
        {t('wishlist:myWishlist.title')}
      </h2>
      <div className="wishlist-items-wrapper">
        {(wishlistItems.length > 0 || !isAnonymous) && (
          <Carousel
            slideIndex={wishlists.findIndex(wishlist => wishlist.active) + 1}
            slideWidth={SlideWidth}
            slideGap={SlideGap}
            className="wishlist-items-carousel"
          >
            {wishlistItems}
          </Carousel>
        )}
        {isAnonymous && <FdrWishlistNoAuthNotification />}
      </div>
      {editedWishlist ? (
        <WishlistEditFormContainer
          id={editedWishlist.id}
          value={editedWishlist.name}
          onClose={() => setEditedWishlist(null)}
          onSubmit={() => setEditedWishlist(null)}
        />
      ) : (
        <WishlistAddFormContainer />
      )}
      <ConfirmationModal
        {...confirmationProps}
        title={t('wishlist:remove.confirmation.title')}
        question={t('wishlist:remove.confirmation.question', {
          name: deletedWishlist?.name,
        })}
        onApply={() => {
          if (deletedWishlist) {
            confirmationProps.onApply(deletedWishlist.id);
          }
        }}
      />
    </WishlistOverviewWrapper>
  );
};
